import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit2,
  Save,
  X,
  Rss,
  FileText,
  Eye,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  Plus,
} from 'lucide-react';
import {
  AdminClientApi,
  ClientFeed,
  FeedContentItem,
  GetFeedContentInputInterface,
} from '../../Admin-ClientLibrary';
import { useRecentEntities } from '../../contexts/RecentEntitiesContext';
import AddContentToFeedModal from '../../components/AddContentToFeedModal';
import './FeedDetail.css';
import { getApiUrl } from '../../config/api';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const FeedDetail: React.FC = () => {
  const { feedId } = useParams<{ feedId: string }>();
  const navigate = useNavigate();
  const { addRecentEntity } = useRecentEntities();

  const [feed, setFeed] = useState<ClientFeed | null>(null);
  const [feedContent, setFeedContent] = useState<FeedContentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [contentLoading, setContentLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalContent, setTotalContent] = useState<number>(0);

  // Edit feed name state
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [editedName, setEditedName] = useState<string>('');

  // Pagination and ordering state
  const [orderBy, setOrderBy] = useState<string>('rank');
  const [orderDirection, setOrderDirection] = useState<string>('DESC');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const limit = 20;

  // Modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);

  // Fetch feed details
  const fetchFeed = async (): Promise<void> => {
    if (!feedId) return;

    try {
      const response = await adminApi.getFeed({ feedId: parseInt(feedId) });

      if (response.success && response.feed) {
        setFeed(response.feed);
        setEditedName(response.feed.title);

        // Add feed to recent entities
        addRecentEntity('feed', response.feed);
      } else {
        setError(response.message || 'Feed not found');
      }
    } catch (err: any) {
      console.error('Error fetching feed:', err);
      setError('Failed to fetch feed details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch feed content
  const fetchFeedContent = async (
    orderByParam?: string,
    orderDirectionParam?: string,
    getCount: boolean = false
  ): Promise<void> => {
    if (!feedId) return;

    setContentLoading(true);
    try {
      const apiParams: GetFeedContentInputInterface = {
        feedId: parseInt(feedId),
        limit,
        orderBy: orderByParam || orderBy,
        orderDirection: orderDirectionParam || orderDirection,
        getCount,
      };

      console.log('Fetching feed content with params:', apiParams);

      const response = await adminApi.getFeedContent(apiParams);

      if (response.success) {
        setFeedContent(response.feedContent);
        if (getCount) {
          setTotalContent(response.total);
        }
      } else {
        setError(response.message || 'Failed to fetch feed content');
        setFeedContent([]);
      }
    } catch (err: any) {
      console.error('Error fetching feed content:', err);
      setError('Network error occurred');
      setFeedContent([]);
    } finally {
      setContentLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    if (feedId) {
      fetchFeed();
      fetchFeedContent(undefined, undefined, true);
    }
  }, [feedId]);

  const handleEditName = (): void => {
    setIsEditingName(true);
  };

  const handleSaveName = (): void => {
    // TODO: Implement API call to update feed name
    console.log('Saving feed name:', editedName);
    if (feed) {
      setFeed({ ...feed, title: editedName });
    }
    setIsEditingName(false);
  };

  const handleCancelEdit = (): void => {
    setEditedName(feed?.title || '');
    setIsEditingName(false);
  };

  const handleOrderByChange = (newOrderBy: string): void => {
    setOrderBy(newOrderBy);
    setCurrentPage(1);
    fetchFeedContent(newOrderBy, orderDirection, true);
  };

  const handleOrderDirectionChange = (newDirection: string): void => {
    setOrderDirection(newDirection);
    setCurrentPage(1);
    fetchFeedContent(orderBy, newDirection, true);
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString();
  };

  const getContentTypeIcon = (contentType: string) => {
    switch (contentType?.toLowerCase()) {
      case 'thought':
        return <FileText size={16} className="content-type-icon thought" />;
      case 'feed':
        return <Rss size={16} className="content-type-icon feed" />;
      default:
        return <FileText size={16} className="content-type-icon default" />;
    }
  };

  const getContentDisplayText = (content: any): string => {
    if (content.text) {
      return content.text.length > 100 ? `${content.text.substring(0, 100)}...` : content.text;
    }
    if (content.title) {
      return content.title;
    }
    return `Content ID: ${content.id}`;
  };

  const handleAddContentSuccess = (feedRank: number) => {
    // Refresh the feed content after successful addition
    fetchFeedContent(undefined, undefined, true);
  };

  if (loading) {
    return (
      <div className="feed-detail-page">
        <div className="loading">Loading feed details...</div>
      </div>
    );
  }

  if (error && !feed) {
    return (
      <div className="feed-detail-page">
        <div className="error-message">
          <p>Error: {error}</p>
          <button className="btn-secondary" onClick={() => navigate('/feeds')}>
            Back to Feeds
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="feed-detail-page">
      {/* Header */}
      <div className="feed-detail-header">
        <div className="header-top">
          <button className="btn-secondary" onClick={() => navigate('/feeds')}>
            <ArrowLeft size={16} />
            Back to Feeds
          </button>
          <div className="feed-info">
            <Rss size={24} className="feed-icon" />
            <div className="feed-meta">
              <span className="feed-id">Feed ID: {feed?.content_id}</span>
              <span className="feed-created">
                Created: {feed ? formatDate(feed.createdAt) : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Feed Name Section */}
        <div className="feed-name-section">
          <h1>Feed Details</h1>
          <div className="feed-name-container">
            {isEditingName ? (
              <div className="edit-name-form">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="name-input"
                  placeholder="Enter feed name"
                  autoFocus
                />
                <div className="edit-actions">
                  <button className="btn-primary" onClick={handleSaveName}>
                    <Save size={16} />
                    Save
                  </button>
                  <button className="btn-secondary" onClick={handleCancelEdit}>
                    <X size={16} />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="name-display">
                <h2>{feed?.title || 'Untitled Feed'}</h2>
                <div className="action-buttons">
                  <button className="btn-secondary edit-btn" onClick={handleEditName}>
                    <Edit2 size={16} />
                    Edit Name
                  </button>
                  <button
                    className="btn-primary simulate-btn"
                    onClick={() => navigate(`/feeds/${feedId}/simulate`)}
                  >
                    <Eye size={16} />
                    Simulate
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Feed Content Section */}
      <div className="feed-content-section">
        <div className="content-header">
          <div className="content-title">
            <h3>Feed Content</h3>
            <span className="content-count">({totalContent} items)</span>
          </div>

          {/* Order Controls and Add Button */}
          <div className="order-controls">
            <button className="btn-primary add-content-btn" onClick={() => setIsAddModalOpen(true)}>
              <Plus size={14} />
              Add from Recent
            </button>

            <div className="order-group">
              <label>Order by:</label>
              <select
                value={orderBy}
                onChange={(e) => handleOrderByChange(e.target.value)}
                className="order-select"
              >
                <option value="rank">Rank</option>
                <option value="createdAt">Created Date</option>
              </select>
            </div>
            <div className="order-group">
              <label>Direction:</label>
              <select
                value={orderDirection}
                onChange={(e) => handleOrderDirectionChange(e.target.value)}
                className="order-select"
              >
                <option value="ASC">
                  <ChevronUp size={16} /> Ascending
                </option>
                <option value="DESC">
                  <ChevronDown size={16} /> Descending
                </option>
              </select>
            </div>
          </div>
        </div>

        {/* Content List */}
        <div className="content-list">
          {contentLoading ? (
            <div className="loading">Loading feed content...</div>
          ) : feedContent.length === 0 ? (
            <div className="empty-state">
              <Rss size={48} />
              <h4>No Content Yet</h4>
              <p>This feed doesn't have any content items yet.</p>
            </div>
          ) : (
            <div className="content-table-container">
              <table className="content-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Type</th>
                    <th>Content</th>
                    <th>Content ID</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {feedContent.map((item) => (
                    <tr key={item.id} className="content-row">
                      <td>
                        <span className="rank-badge">{item.feed_rank}</span>
                      </td>
                      <td>
                        <div className="content-type">
                          {getContentTypeIcon(item.content.type)}
                          <span>{item.content.type || 'Unknown'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="content-preview">{getContentDisplayText(item.content)}</div>
                      </td>
                      <td>
                        <span className="content-id">{item.content.id}</span>
                      </td>
                      <td>{formatDate(item.createdAt)}</td>
                      <td>
                        <div className="actions">
                          <button className="action-btn" title="View Details">
                            <Eye size={14} />
                          </button>
                          <button className="action-btn" title="More Options">
                            <MoreVertical size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Content Modal */}
      <AddContentToFeedModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        feedId={parseInt(feedId || '0')}
        onSuccess={handleAddContentSuccess}
      />
    </div>
  );
};

export default FeedDetail;
